import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section } from "../components/site/blocks";
import { api } from "../lib/api";
import { euros } from "../data/site";

export function PaymentSuccess() {
  useSeo("Paiement", "Confirmation de paiement Habitalys.");
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [data, setData] = useState(null);
  const [state, setState] = useState("checking");
  useEffect(() => {
    if (!sid) return setState("error");
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        setData(r.data);
        if (r.data.status === "payé") return setState("paid");
        if (["échoué", "expiré", "annulé"].includes(r.data.status)) return setState("failed");
      } catch { return setState("error"); }
      if (++tries >= 10 || stop) return setState("timeout");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sid]);

  return (
    <Section className="bg-mist" testid="payment-success-page">
      <div className="max-w-2xl rounded-[28px] bg-white border border-navy/10 p-8 sm:p-12">
        {state === "checking" && <p className="flex items-center gap-3 text-lg text-slate-700" data-testid="payment-checking"><Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" /> Vérification de votre paiement auprès de Stripe…</p>}
        {state === "paid" && data && (
          <div data-testid="payment-success">
            <CheckCircle2 className="h-12 w-12 text-green-700" aria-hidden="true" />
            <h1 className="mt-5 text-3xl sm:text-4xl">Merci, votre paiement a bien été reçu</h1>
            <dl className="mt-6 space-y-2 text-lg">
              <div><dt className="inline text-slate-600">Montant : </dt><dd className="inline font-semibold text-navy" data-testid="payment-success-amount">{euros(data.amount_cents)} TTC</dd></div>
              <div><dt className="inline text-slate-600">Référence : </dt><dd className="inline font-semibold text-navy" data-testid="payment-success-reference">{data.reference}</dd></div>
              <div><dt className="inline text-slate-600">Objet : </dt><dd className="inline">{data.description}</dd></div>
            </dl>
            <p className="mt-6 text-slate-700">Une confirmation vous est envoyée par email. Un conseiller vous recontacte sous 48 h ouvrées.</p>
            <Link to="/" className="btn-primary mt-8">Retour à l'accueil</Link>
          </div>
        )}
        {["failed", "timeout", "error"].includes(state) && (
          <div data-testid="payment-not-confirmed">
            <AlertTriangle className="h-12 w-12 text-amber-700" aria-hidden="true" />
            <h1 className="mt-5 text-3xl">{state === "timeout" ? "Paiement en cours de confirmation" : "Paiement non abouti"}</h1>
            <p className="mt-4 text-slate-700">{state === "timeout" ? "La confirmation de Stripe prend plus de temps que prévu. Vous recevrez un email dès qu'elle sera reçue ; inutile de payer à nouveau." : "Nous n'avons pas pu confirmer ce paiement. Aucun montant n'a été validé."}</p>
            <Link to="/tarifs" className="btn-primary mt-8">Retour aux tarifs</Link>
          </div>
        )}
      </div>
    </Section>
  );
}

export function PaymentCancel() {
  useSeo("Paiement non abouti", "Le paiement n'a pas abouti.");
  const [params] = useSearchParams();
  const pid = params.get("pid");
  const [p, setP] = useState(null);
  useEffect(() => { if (pid) api.get(`/payments/${pid}/public`).then((r) => setP(r.data)).catch(() => {}); }, [pid]);
  const retry = p?.type === "acompte" && p.token ? `/paiement/${p.token}` : p?.forfait_id ? `/tarifs/${p.forfait_id}/paiement` : "/tarifs";
  return (
    <Section className="bg-mist" testid="payment-cancel-page">
      <div className="max-w-2xl rounded-[28px] bg-white border border-navy/10 p-8 sm:p-12">
        <AlertTriangle className="h-12 w-12 text-amber-700" aria-hidden="true" />
        <h1 className="mt-5 text-3xl sm:text-4xl">Paiement non abouti</h1>
        <p className="mt-4 text-lg text-slate-700">Le paiement a été interrompu ou refusé : aucun montant n'a été débité. Vous pouvez réessayer quand vous le souhaitez.</p>
        {p && <p className="mt-3 text-slate-700">Référence : <strong>{p.reference}</strong> — {euros(p.amount_cents)} TTC</p>}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={retry} className="btn-accent" data-testid="payment-retry-button"><RotateCcw className="h-5 w-5" aria-hidden="true" /> Réessayer le paiement</Link>
          <Link to="/contact" className="btn-outline">Nous contacter</Link>
        </div>
      </div>
    </Section>
  );
}
